import React from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { userBrandsList } from '@/store/slices/casino-instance';
import {
  fetchCasinoDomainInfo,
  selectCasinoDomainInfo,
  selectCasinoDomainInfoLoading,
  selectCasinoDomainInfoError,
} from '@/store/slices/casinoDomainInfo';

import { Toast } from '../../components';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { useShowInProgress } from '../../hooks/useShowInProgress';
import { useToast } from '../../hooks/useToast';
import { buildCasinoSettings, CASINO_VARIANT_COUNT } from '../../mock-data/casino-settings';
import type { CasinoDomain, CasinoKind } from '../../mock-data/types';
import {
  DomainsSection,
  SearchConsoleSection,
  CountryFilterSection,
  CasinoTypeSection,
  TechnicalDetailsSection,
  DnsModal,
} from './components';
import { resolveCasinoDomains } from './domains';
import { resolveTechnicalDetails } from './technical';
import { Page } from './casino-settings.styles';

/**
 * Casino Settings: manage the casino's domains and casino type.
 *
 * Mounted as the Website Settings section of Settings (`/account/settings/website-settings`); the
 * `SettingsSectionPage` dispatcher provides the surrounding chrome (PageShell + BackHeader), so this renders
 * only the section body. The logos used to live here too and moved to their own Website tab
 * (`pages/logos`, `/account/website/logos`); their section, dialog and pipeline modules stay in this folder.
 *
 * The casino type is still mocked via `buildCasinoSettings`. The domain list is fully real, fetched per brand
 * from the `/casino-instance/domain-info` API (the `casinoDomainInfo` slice) and shaped by
 * `resolveCasinoDomains`. There is NO mock fallback: brands with no usable record render the section's own
 * "unknown" state instead of fabricated domains. The active brand comes from the `casinoInstance` slice (set
 * by the brand switcher); each brand maps to one of the stand-in mock datasets by its list position (cycling,
 * exactly like Home), so switching brands walks through every state the page can show. When the remaining
 * APIs land, swap the mock derivation for real per-brand selectors yielding the same shapes and delete
 * `mock-data/casino-settings`.
 */
export const CasinoSettingsSection = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const brands = useAppSelector((state) => state.casinoInstance.brands, isEqual);
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand, isEqual);
  const brandsLoading = useAppSelector((state) => state.casinoInstance.loading);
  const brandsLoaded = useAppSelector((state) => state.casinoInstance.loaded);

  // Website Currency + Technical details are still backed by data we can't fully wire
  // yet (the casino type is mocked; the deploy hero/identifiers are a behind-the-scenes
  // view), so both stay hidden behind the `show_in_progress` flag until then.
  const showInProgress = useShowInProgress();
  // Country access stores a filter that no service applies yet, so it ships behind its own flag.
  const { isEnabled } = useFeatureGate();

  // The Technical-details section reads its data straight off the selected brand's
  // real `casino-instance` record (no mock). MainLayout already loads the brand
  // list app-wide; this guarded one-shot makes the page self-sufficient if it's
  // ever shown without that chrome, and is a no-op once loaded/loading.
  const requestedBrands = React.useRef(false);
  React.useEffect(() => {
    if (requestedBrands.current) return;
    requestedBrands.current = true;
    if (!brandsLoaded && !brandsLoading) dispatch(userBrandsList());
  }, [dispatch, brandsLoaded, brandsLoading]);

  // The Technical-details view derived from the active brand's real record.
  const technicalDetails = React.useMemo(() => resolveTechnicalDetails(displayBrand), [displayBrand]);

  const variant = React.useMemo<number>(() => {
    if (!displayBrand) return 0;
    const idx = brands.findIndex((brand) => brand.id === displayBrand.id);
    return ((Math.max(0, idx) % CASINO_VARIANT_COUNT) + CASINO_VARIANT_COUNT) % CASINO_VARIANT_COUNT;
  }, [brands, displayBrand]);

  const data = React.useMemo(() => buildCasinoSettings(variant), [variant]);

  // The casino's live web address, real, straight off the active brand. Drives the
  // "Visit site" link on the Website Domain card; undefined for brands with none yet.
  const liveDomain = displayBrand?.casino_data?.details?.domain?.trim() || undefined;

  // The brand's tenant, for the spark-creatives calls (Google Search Console). Same derivation as Stats → Website.
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;

  // The casino type still loads from a mock endpoint on a randomised delay. Swapping in a
  // real API means replacing the call with a query hook of the same `{ data, loading }`
  // shape; the section doesn't change.
  const typeEndpoint = useMockEndpoint(data.kind);

  // The Website Domain list is REAL: the `/casino-instance/domain-info` API, fetched
  // once per brand (keyed by instance id) and shaped by `resolveCasinoDomains`. There
  // is no mock fallback. When the API carries no usable record (the brand returns
  // DOMAIN_NOT_FOUND, or the response can't be parsed) `resolveCasinoDomains` yields
  // `null` and the section renders its own "unknown" state, never fabricated domains.
  const instanceId = displayBrand?.id;
  const domainInfoRaw = useAppSelector((state) => selectCasinoDomainInfo(state, instanceId));
  const domainInfoLoading = useAppSelector((state) => selectCasinoDomainInfoLoading(state, instanceId));
  const domainInfoError = useAppSelector((state) => selectCasinoDomainInfoError(state, instanceId));
  const fetchedDomainInfo = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!instanceId || fetchedDomainInfo.current.has(instanceId)) return;
    fetchedDomainInfo.current.add(instanceId);
    dispatch(fetchCasinoDomainInfo({ casinoInstanceId: instanceId }));
  }, [dispatch, instanceId]);

  // `null` when the API carries no usable record → the section shows its unknown state.
  const apiDomains = React.useMemo(() => resolveCasinoDomains(domainInfoRaw, liveDomain), [domainInfoRaw, liveDomain]);
  const domainsData = apiDomains ?? [];
  // Hold the skeleton until the brand and its domain request both settle. A brand's
  // domain info is resolved once the API returns either a payload OR an error (e.g.
  // DOMAIN_NOT_FOUND); keeping the skeleton until then stops the unknown state from
  // flashing in the gap between the brand arriving and the fetch starting/finishing.
  const domainInfoResolved = domainInfoRaw !== undefined || domainInfoError !== undefined;
  const domainsLoading =
    (!displayBrand && brandsLoading) || domainInfoLoading || (Boolean(instanceId) && !domainInfoResolved);

  const { toast, show, dismiss } = useToast();
  const onToast = React.useCallback(
    (message: string, placeholders?: Record<string, string | number>) =>
      show(message, placeholders ? { placeholders } : undefined),
    [show],
  );

  // Casino type — local selection seeded from the active brand's saved value. A
  // deliberate pick-then-Save (mirrors Settings → Profile), re-seeded when the brand
  // (and thus the saved value) changes.
  const [kind, setKind] = React.useState<CasinoKind>(data.kind);
  const [kindBaseline, setKindBaseline] = React.useState<CasinoKind>(data.kind);
  const [justSaved, setJustSaved] = React.useState(false);
  const savedTimer = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    // Cancel any in-flight "saved" confirmation so switching brands can't leave a
    // pending timer to flip justSaved back off mid-flicker.
    window.clearTimeout(savedTimer.current);
    setKind(data.kind);
    setKindBaseline(data.kind);
    setJustSaved(false);
  }, [data.kind]);

  React.useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  const onSelectKind = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    const id = event.currentTarget.dataset.id;
    if (id === 'fiat' || id === 'crypto' || id === 'mixed') {
      setKind(id);
      setJustSaved(false);
    }
  }, []);

  const onSaveKind = React.useCallback(() => {
    setKindBaseline(kind);
    setJustSaved(true);
    onToast(t('Casino type updated'));
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setJustSaved(false), 1800);
  }, [kind, onToast, t]);

  // DNS settings overlay (an unmanaged domain).
  const [dnsDomain, setDnsDomain] = React.useState<CasinoDomain | null>(null);

  const onViewDns = React.useCallback((domain: CasinoDomain) => {
    // Only unmanaged domains carry DNS records — guard so the modal never opens empty.
    if (domain.dns && domain.dns.length > 0) {
      setDnsDomain(domain);
    }
  }, []);
  const onDnsClose = React.useCallback(() => setDnsDomain(null), []);

  return (
    <>
      <Page>
        {showInProgress ? (
          <CasinoTypeSection
            value={kind}
            saved={kindBaseline}
            dirty={kind !== kindBaseline}
            justSaved={justSaved}
            loading={typeEndpoint.loading}
            onSelect={onSelectKind}
            onSave={onSaveKind}
          />
        ) : null}
        <DomainsSection domains={domainsData} loading={domainsLoading} liveDomain={liveDomain} onViewDns={onViewDns} />
        {isEnabled('country-filter') ? <CountryFilterSection instanceId={instanceId} onToast={onToast} /> : null}
        <SearchConsoleSection tenantId={tenantId} onToast={onToast} />
        {showInProgress ? (
          <TechnicalDetailsSection
            details={technicalDetails}
            loading={!displayBrand && brandsLoading}
            hasBrand={Boolean(displayBrand)}
            onToast={onToast}
          />
        ) : null}
      </Page>

      {dnsDomain ? <DnsModal domain={dnsDomain} onClose={onDnsClose} onToast={onToast} /> : null}

      <Toast toast={toast} onDismiss={dismiss} />
    </>
  );
};

export default CasinoSettingsSection;
